import { pb } from "./pocketbase";
import { financeiroIntegracaoService } from "./financeiroIntegracaoService";

const parseCurrency = (value) => {
  if (!value) return 0;
  try {
    if (typeof value === "number") return value;
    let str = String(value).trim().replace("R$", "").trim();
    if (str.includes(",")) str = str.replace(/\./g, "").replace(",", ".");
    const parsed = parseFloat(str);
    return isNaN(parsed) ? 0 : parsed;
  } catch (e) {
    return 0;
  }
};

export const getColabRules = (name) => {
  const normName = String(name).trim().toUpperCase();
  if (normName === "CACO") {
    return { maxPoints: 60, maxBonus: 500 };
  } else if (normName === "CAMILLE") {
    return { maxPoints: 40, maxBonus: 500 };
  } else {
    return { maxPoints: 10, maxBonus: 300 };
  }
};

export const pontuacaoService = {
  // --- 1. CONFIGURAÇÃO DA META MENSAL ---
  async fetchMonthlyGoal(mes, ano) {
    try {
      const records = await pb.collection("pontuacao_metas").getList(1, 1, {
        filter: `mes = ${Number(mes)} && ano = ${Number(ano)}`
      });
      return records.items[0] || null;
    } catch (err) {
      console.warn(`Error fetching monthly goal for ${mes}/${ano}:`, err);
      return null;
    }
  },

  async saveMonthlyGoal(mes, ano, metaVal) {
    try {
      const existing = await this.fetchMonthlyGoal(mes, ano);
      const payload = {
        mes: Number(mes),
        ano: Number(ano),
        meta: Number(metaVal),
        status: "aberta"
      };

      if (existing) {
        return await pb.collection("pontuacao_metas").update(existing.id, payload);
      } else {
        return await pb.collection("pontuacao_metas").create(payload);
      }
    } catch (err) {
      console.error("Error saving monthly goal:", err);
      throw err;
    }
  },

  // --- 2. GESTÃO DE OCORRÊNCIAS ---
  async fetchOccurrences(colaboradorId, mes, ano) {
    try {
      let filter = `ano = ${Number(ano)}`;
      if (Number(mes) > 0) {
        filter += ` && mes = ${Number(mes)}`;
      }
      if (colaboradorId) {
        filter += ` && colaborador = "${colaboradorId}"`;
      }
      
      return await pb.collection("pontuacao_ocorrencias").getFullList({
        filter: filter,
        expand: "colaborador,responsavel",
        sort: "-data"
      });
    } catch (err) {
      console.error("Error fetching occurrences:", err);
      return [];
    }
  },

  async createOccurrence(data) {
    try {
      return await pb.collection("pontuacao_ocorrencias").create(data);
    } catch (err) {
      console.error("Error creating occurrence:", err);
      throw err;
    }
  },

  async deleteOccurrence(id) {
    try {
      return await pb.collection("pontuacao_ocorrencias").delete(id);
    } catch (err) {
      console.error("Error deleting occurrence:", err);
      throw err;
    }
  },

  // --- CÁLCULO DE FATURAMENTO ACUMULADO ---
  async fetchMonthlyFaturamento(mes, ano) {
    const pad = (n) => String(n).padStart(2, "0");
    const startIso = `${ano}-${pad(mes)}-01T00:00:00.000Z`;
    const lastDay = new Date(ano, mes, 0).getDate();
    const endIso = `${ano}-${pad(mes)}-${pad(lastDay)}T23:59:59.999Z`;

    try {
      const prodRecords = await pb.collection("production_orders").getFullList({
        filter: `(created >= "${startIso}" && created <= "${endIso}") || (full_data.convertedAt >= "${startIso}" && full_data.convertedAt <= "${endIso}")`,
        fields: "id,total_value,full_data,created,order_number,status"
      });

      const ordersMap = new Map();

      prodRecords.forEach(r => {
        const orderNum = r.order_number || r.full_data?.order?.number || r.full_data?.orderNumber || "S/N";
        if (!ordersMap.has(orderNum)) {
          ordersMap.set(orderNum, {
            paymentTotal: parseCurrency(r.full_data?.order?.payment?.total || r.full_data?.total_value || r.total_value || 0),
            shippingPrice: parseCurrency(r.full_data?.order?.shipping?.price || r.full_data?.shipping?.price || 0),
            items: []
          });
        }

        const isCancelled = r.status === "CANCELADO" || r.full_data?.logisticsStatus === "CANCELADO";
        const isGolpe = r.status === "GOLPE" || r.full_data?.logisticsStatus === "GOLPE";

        ordersMap.get(orderNum).items.push({
          fromStock: !!r.full_data?.fromStock,
          isCancelled,
          isGolpe,
          convertedAtStr: r.full_data?.convertedAt || r.created
        });
      });

      let total = 0;

      ordersMap.forEach((info) => {
        const netOrderTotal = Math.max(0, info.paymentTotal - info.shippingPrice);
        
        const targetMonthFactoryItems = info.items.filter(item => {
          if (item.fromStock || item.isCancelled || item.isGolpe) return false;
          
          if (item.convertedAtStr) {
            const d = new Date(item.convertedAtStr);
            return (d.getMonth() + 1 === Number(mes) && d.getFullYear() === Number(ano));
          }
          return false;
        });

        if (targetMonthFactoryItems.length > 0) {
          const totalItemsCount = info.items.length || 1;
          const proportionalVal = (netOrderTotal / totalItemsCount) * targetMonthFactoryItems.length;
          total += proportionalVal;
        }
      });

      return total;
    } catch (err) {
      console.warn("Could not retrieve production_orders for monthly faturamento:", err.message);
      return 0;
    }
  },

  // --- 4. CONFIGURAÇÃO DE EQUIPES & BÔNUS (pontuacao_colaboradores) ---
  async fetchColaboradoresConfig() {
    try {
      return await pb.collection("pontuacao_colaboradores").getFullList({
        expand: "colaborador,lider",
        sort: "colaborador.name"
      });
    } catch (err) {
      console.warn("Collection pontuacao_colaboradores not found or error fetching. Falling back to local storage.", err);
      const local = localStorage.getItem("pontuacao_colaboradores_local");
      if (local) {
        const list = JSON.parse(local);
        return list.map(item => ({ ...item, isLocal: true }));
      }
      return [];
    }
  },

  async saveColaboradorConfig(userId, data) {
    try {
      let existing = null;
      try {
        const records = await pb.collection("pontuacao_colaboradores").getList(1, 1, {
          filter: `colaborador = "${userId}"`
        });
        existing = records.items[0] || null;
      } catch (e) {}

      const payload = {
        colaborador: userId,
        lider: data.lider || null,
        is_lider: !!data.is_lider,
        bonus_base: Number(data.bonus_base || 0),
        data_admissao: data.data_admissao || null
      };

      if (existing) {
        return await pb.collection("pontuacao_colaboradores").update(existing.id, payload);
      } else {
        return await pb.collection("pontuacao_colaboradores").create(payload);
      }
    } catch (err) {
      console.warn("Failed to save to PocketBase. Saving to local storage fallback.", err);
      const local = localStorage.getItem("pontuacao_colaboradores_local");
      let list = local ? JSON.parse(local) : [];
      
      const existingIdx = list.findIndex(r => r.colaborador === userId);
      const mockRecord = {
        id: existingIdx >= 0 ? list[existingIdx].id : "mock_" + Math.random().toString(36).substr(2, 9),
        colaborador: userId,
        lider: data.lider || null,
        is_lider: !!data.is_lider,
        bonus_base: Number(data.bonus_base || 0),
        data_admissao: data.data_admissao || null,
        isLocal: true,
        expand: {
          colaborador: { id: userId, name: data.colaboradorName || "Usuário" },
          lider: data.lider ? { id: data.lider, name: data.liderName || "Líder" } : null
        }
      };

      if (existingIdx >= 0) {
        list[existingIdx] = mockRecord;
      } else {
        list.push(mockRecord);
      }
      localStorage.setItem("pontuacao_colaboradores_local", JSON.stringify(list));
      return mockRecord;
    }
  },

  // --- 5. CONTROLE DE PAGAMENTOS DE BÔNUS (pontuacao_pagamentos) ---
  async fetchPayments(mes, ano) {
    try {
      return await pb.collection("pontuacao_pagamentos").getFullList({
        filter: `mes = ${Number(mes)} && ano = ${Number(ano)}`
      });
    } catch (err) {
      console.warn("Collection pontuacao_pagamentos not found or error fetching. Falling back to local storage.", err);
      const localKey = `pontuacao_pagamentos_${mes}_${ano}`;
      const local = localStorage.getItem(localKey);
      return local ? JSON.parse(local) : [];
    }
  },

  async savePayment(colaboradorId, mes, ano, valor, status) {
    try {
      let existing = null;
      let oldRecord = null;
      try {
        const records = await pb.collection("pontuacao_pagamentos").getList(1, 1, {
          filter: `colaborador = "${colaboradorId}" && mes = ${Number(mes)} && ano = ${Number(ano)}`
        });
        oldRecord = records.items[0] || null;
      } catch (e) {}

      const payload = {
        colaborador: colaboradorId,
        mes: Number(mes),
        ano: Number(ano),
        valor_pago: Number(valor),
        status: status, // "pago" | "pendente"
        data_pagamento: new Date().toISOString()
      };

      let result = null;
      if (oldRecord) {
        result = await pb.collection("pontuacao_pagamentos").update(oldRecord.id, payload);
      } else {
        result = await pb.collection("pontuacao_pagamentos").create(payload);
      }

      // Gatilho Financeiro se salvou com sucesso no PocketBase
      if (result) {
        const wasPago = oldRecord ? oldRecord.status === "pago" : false;
        const isPago = result.status === "pago";

        if (!wasPago && isPago) {
          // Dispara despesa para o Financeiro
          try {
            let colName = "Colaborador ID: " + colaboradorId;
            try {
              const col = await pb.collection("colaboradores").getOne(colaboradorId, { fields: "nome" });
              if (col) colName = col.nome;
            } catch (e) {}

            await financeiroIntegracaoService.triggerPendingTransaction({
              origem: "pontuacao_pagamentos",
              origemId: result.id,
              snapshot: result,
              dataSugerida: result.data_pagamento ? result.data_pagamento.split("T")[0] : new Date().toISOString().split("T")[0],
              valorSugeridoCentavos: Math.round(Number(result.valor_pago) * 100),
              descricaoSugerida: `Bônus de Desempenho - ${colName} (${result.mes}/${result.ano})`,
              tipoPendencia: "estorno",
            });
          } catch (errFin) {
            console.warn("Erro ao gerar pendência financeira de bônus:", errFin);
          }
        } else if (wasPago && !isPago) {
          // Cancelamento de pagamento
          try {
            await financeiroIntegracaoService.triggerCancellation("pontuacao_pagamentos", result.id, pb.authStore.model);
          } catch (errFin) {
            console.warn("Erro ao cancelar pendência de bônus:", errFin);
          }
        }
      }

      return result;
    } catch (err) {
      console.warn("Failed to save payment to PocketBase. Saving to local storage fallback.", err);
      const localKey = `pontuacao_pagamentos_${mes}_${ano}`;
      const local = localStorage.getItem(localKey);
      let list = local ? JSON.parse(local) : [];

      const existingIdx = list.findIndex(r => r.colaborador === colaboradorId);
      const mockRecord = {
        id: existingIdx >= 0 ? list[existingIdx].id : "mock_pay_" + Math.random().toString(36).substr(2, 9),
        colaborador: colaboradorId,
        mes: Number(mes),
        ano: Number(ano),
        valor_pago: Number(valor),
        status: status,
        data_pagamento: new Date().toISOString()
      };

      if (existingIdx >= 0) {
        list[existingIdx] = mockRecord;
      } else {
        list.push(mockRecord);
      }
      localStorage.setItem(localKey, JSON.stringify(list));
      return mockRecord;
    }
  },

  async fetchYearlyProductionOrders(ano) {
    const startIso = `${ano}-01-01T00:00:00.000Z`;
    const endIso = `${ano}-12-31T23:59:59.999Z`;
    try {
      return await pb.collection("production_orders").getFullList({
        filter: `(created >= "${startIso}" && created <= "${endIso}") || (full_data.convertedAt >= "${startIso}" && full_data.convertedAt <= "${endIso}")`,
        fields: "id,total_value,full_data,created,order_number,status"
      });
    } catch (err) {
      console.warn("Could not retrieve yearly production_orders:", err.message);
      return [];
    }
  },

  async fetchYearlyGoals(ano) {
    try {
      return await pb.collection("pontuacao_metas").getFullList({
        filter: `ano = ${Number(ano)}`
      });
    } catch (err) {
      console.warn(`Error fetching yearly goals for ${ano}:`, err);
      return [];
    }
  },

  async fetchYearlyPayments(ano) {
    try {
      return await pb.collection("pontuacao_pagamentos").getFullList({
        filter: `ano = ${Number(ano)}`
      });
    } catch (err) {
      console.warn("Failed to fetch yearly payments from PocketBase. Reading from local storage fallback.", err);
      const list = [];
      for (let m = 1; m <= 12; m++) {
        const local = localStorage.getItem(`pontuacao_pagamentos_${m}_${ano}`);
        if (local) {
          list.push(...JSON.parse(local));
        }
      }
      return list;
    }
  }
};
